import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { CloseIcon } from '../../atoms/icons/CloseIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { TertiaryButton } from '../../atoms/tertiaryButton/TertiaryButton'
import { Title } from '../../atoms/title/Title'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

type AlertPopUpButton = {
  text: React.ReactNode
  onPress: () => void
  icon?: React.ReactElement
  testID?: string
  loading?: boolean
}
export type AlertPopUpProps = {
  ariaLabel?: string
  headerText?: React.ReactNode
  icon?: React.ReactElement
  onClose: () => void
  secondaryButton?: AlertPopUpButton
  tertiaryButton?: AlertPopUpButton
  text: React.ReactNode
  textSize?: BodyTextSize
  style?: ViewStyle
}

const AlertPopUpContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundWarning,
  borderRadius: theme.spacing['16px'],
  color: theme.colors.borderWarning,
  display: 'flex',
  padding: theme.spacing['16px'],
  maxWidth: '400px',
  boxShadow: `0 2px 10px ${theme.colors.shadowLow}`,
}))

const CloseIconContainer = styled(PressableOpacity)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing['12px'],
  right: theme.spacing['12px'],
  zIndex: 1,
}))

const ContentContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const IconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignSelf: 'flex-start',
  marginTop: theme.spacing['4px'],
}))

const TextContainer = styled.View({
  flex: 1,
})

const HeaderTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['8px'],
}))

const ButtonsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  flexDirection: 'row',
  marginTop: theme.spacing['12px'],
  alignSelf: 'flex-end',
}))

export const AlertPopUp: FunctionComponent<AlertPopUpProps> = ({
  ariaLabel,
  headerText,
  icon,
  onClose,
  secondaryButton,
  tertiaryButton,
  text,
  textSize = BodyTextSize.SMALL,
  style,
}) => {
  const { colors } = useTheme()

  const handleCloseModal = () => {
    onClose()
  }

  const { formatMessage } = useIntl()

  return (
    <AlertPopUpContainer aria-label={ariaLabel} role='alert' testID={tID('AlertPopUp')} style={style} tabIndex={0}>
      <CloseIconContainer
        onPress={handleCloseModal}
        testID={tID('AlertPopUp-close')}
        accessibilityLabel={formatMessage({
          defaultMessage: 'Close Alert',
          description: 'This button closes the alert pop up',
        })}
      >
        <CloseIcon size={12} />
      </CloseIconContainer>
      <ContentContainer>
        {icon && <IconContainer>{icon}</IconContainer>}
        <TextContainer>
          {headerText && (
            <HeaderTextContainer>
              <Title size={TitleSize.SMALL} text={headerText} color={colors.borderWarning} />
            </HeaderTextContainer>
          )}
          <BodyText text={text} size={textSize} color={colors.borderWarning} />
        </TextContainer>
      </ContentContainer>
      {secondaryButton || tertiaryButton ? (
        <ButtonsContainer>
          {tertiaryButton && (
            <TertiaryButton
              customTextColor={colors.borderWarning}
              fullWidth={true}
              onPress={tertiaryButton.onPress}
              testID={tertiaryButton.testID}
              text={tertiaryButton.text}
              style={{ backgroundColor: colors.backgroundWarning }}
              loading={tertiaryButton.loading}
            />
          )}
          {secondaryButton && (
            <SecondaryButton
              customTextColor={colors.borderWarning}
              fullWidth={true}
              onPress={secondaryButton.onPress}
              iconColor={colors.borderWarning}
              rightIcon={secondaryButton.icon}
              testID={secondaryButton.testID}
              text={secondaryButton.text}
              style={{ borderColor: colors.borderWarning, backgroundColor: colors.backgroundWarning }}
              loading={secondaryButton.loading}
            />
          )}
        </ButtonsContainer>
      ) : null}
    </AlertPopUpContainer>
  )
}
